<template>
    <div :class="props.class">
        <ConfirmationModal v-if="askConfirmation" :title="confirmationTitle" :text="confirmationText" @emitResponse="checkConfirmationResponse" />
        <LoadingDialog v-if="loading" />
        
        <Dialog 
            v-model:visible="lessonAnswersSaveVisible" 
            modal 
            header="Gerenciar respostas" 
            :draggable="false" 
            :style="{ width: '50rem' }"
            @hide="closeDialog()"
        >
            <div class="my-5">
                <LoaderComponent v-if="loadingCourseModuleVideoLessonAnwsers" />
                <div v-if="!loadingCourseModuleVideoLessonAnwsers">
                    <p v-if="courseModuleVideoLessonAnswers.length < 1">Nenhuma resposta</p>
                    <template v-for="(lessonAnswer, index) of courseModuleVideoLessonAnswers" :key="index">
                        <div 
                            class="p-2 my-2 border border-iron rounded flex justify-between text-white font-semibold flex-wrap flex-col md:flex-nowrap md:flex-row bg-blueberry"
                            :class="lessonAnswer.id == selectedLessonAnswerId ? 'selected_answer' : ''"
                        >
                            <div class="w-full md:w-auto">
                                <div>
                                    Resposta:{{ lessonAnswer.answer }}
                                </div>
                                <div v-if="lessonAnswer.justification">
                                    Justificativa:{{ lessonAnswer.justification }}
                                </div>
                            </div> 
                            <div class="flex flex-wrap justify-center md:justify-between w-full md:w-auto md:flex-row mt-5 mb:mt-0">
                                <Button
                                    :icon="'pi pi-check-square'"
                                    v-tooltip.top="'Resposta correta'" 
                                    class="cursor-auto correct-button"
                                    v-if="lessonAnswer.correct"
                                />
                                <Button
                                    class="no-bg-button"
                                    :class="lessonAnswer.correct ? 'mx-3' : 'mr-3'"
                                    :icon="'pi pi-pen-to-square'"
                                    @click="editLessonAnwser(lessonAnswer.id)"
                                    v-tooltip.top="'Editar resposta'" 
                                />
                                <Button
                                    class="no-bg-button"
                                    :icon="'pi pi-trash'"
                                    @click="removeLessonAnswer(lessonAnswer.id)"
                                    v-tooltip.top="'Remover resposta'" 
                                />
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <div class="w-full text-left mb-5">
                <Button :label="'Nova resposta'" class="no-bg-button" @click.prevent="resetSelectedLessonAnswer()" />
            </div>
            <div class="flex items-center gap-4 mb-8">
                <label for="answer" class="font-semibold w-24">Resposta <RequiredMarker :isValid="resourseDataToValidate.answer" /></label>
                <InputText 
                    v-model="selectedLessonAnswer.answer" 
                    id="answer" 
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500" 
                />
            </div>
            <div class="flex items-center gap-4 mb-8">
                <label for="justification" class="font-semibold w-24">Justificativa</label>
                <InputText 
                    v-model="selectedLessonAnswer.justification" 
                    id="justification" 
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500" 
                />
            </div>
            <small>A justificativa aparecerá quando o usuário selecionar essa resposta como verdadeira!</small>
            <div class="flex items-center gap-4 mb-8 mt-8 hidden">
                <label for="correct" class="font-semibold w-24">Resposta correta?</label>
                <ToggleSwitch v-model="selectedLessonAnswer.correct" :input-id="'correct'" />
            </div>
            <div class="flex justify-end gap-2">
                <Button label="Cancel" severity="secondary" @click="closeDialog()"></Button>
                <Button class="no-bg-button" label="Save" @click.prevent="saveLessonAnswers()"></Button>
            </div>
        </Dialog>
    </div>
</template>
<script setup>
    import { defineProps, onMounted, defineEmits, reactive, ref, watch } from 'vue';
    import { useToast } from 'primevue/usetoast';
    import { showToast } from '@/composables/toast';
    import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
    import ConfirmationModal from '@/components/Utils/ConfirmationModal.vue';
    import Dialog from 'primevue/dialog';
    import Button from 'primevue/button';
    import InputText from 'primevue/inputtext';
    import { isTextValid } from '@/composables/textUtils';
    import utils from '@/composables/utils';
    import ToggleSwitch from 'primevue/toggleswitch';
    import RequiredMarker from '@/components/misc/RequiredMarkerComponent.vue';
    import CourseVideoLessonAnswerService from '@/services/CourseVideoLessonAnswerService';

    const toast = useToast();
    const props = defineProps({
        lessonId: {
            type: Number,
            required: true
        },
        class: {
            type: String,
            required: false,
            default: ''
        }
    });

    let lessonId = ref(null);

    let loading = ref(false);
    let lessonAnswersSaveVisible = ref(false);
    let moduleVideoResourse = ref({
        title: null,
        file: null,
        course_module_video: null
    });

    let selectedLessonAnswer = ref({
        id: null, 
        correct: false,
        lesson: null,
        answer: null,
        justification: null
    });

    const emit = defineEmits(["actionPerformed"]);

    onMounted(() => {
        lessonId.value = props.lessonId;
        isVisible();
        getCourseModuleVideoLessonAnswers();
        resetVideoResourceObject();
    });

    function isVisible(){
        lessonAnswersSaveVisible.value = lessonId.value != null ? true : false;
    }

    function closeDialog() {
        lessonId.value = null;
        lessonAnswersSaveVisible.value = false;
        emit('closed', true);
    }

    watch(() => props.lessonId, (newValue) => {
        lessonId.value = newValue;
        isVisible();
        getCourseModuleVideoLessonAnswers();
        resetVideoResourceObject();
    },
        { deep: true }
    );

    let resourseDataToValidate = reactive({
        answer: true
    })
    let resourseErrorMessages = {
        answer: 'Por favor informe a resposta'
    }
    async function saveLessonAnswers(){
        let isValid = true;
        for(let attr of Object.keys(resourseDataToValidate)){
            if(!isTextValid(selectedLessonAnswer.value[attr])){
                isValid = false;
                resourseDataToValidate[attr] = false;
                showToast(toast, resourseErrorMessages[attr], 'error');
                break;
            }else{
                resourseDataToValidate[attr] = true;
            }
        }
        if(!isValid){
            return;
        }
        let videoLessonAnswer = utils.copyVariable(selectedLessonAnswer.value);
        if(videoLessonAnswer.lesson == null)
            videoLessonAnswer.lesson = lessonId.value;

        loading.value = true;
        let lessonAnswerSaveResult = await CourseVideoLessonAnswerService.saveLessonAnswer(videoLessonAnswer, selectedLessonAnswerId.value);
        loading.value = false;

        if(!lessonAnswerSaveResult.success()){
            showToast(toast, lessonAnswerSaveResult.getMessage(), 'error');
            return;
        }
        showToast(toast, 'Resposta da lição adicionado');
        getCourseModuleVideoLessonAnswers(true);
        resetVideoResourceObject();
    }

    function resetVideoResourceObject(){
        moduleVideoResourse.value = {
            title: null,
            file: null,
            course_module_video: null
        };

        selectedLessonAnswer.value = {
            id: null, 
            correct: false,
            lesson: null,
            answer: null,
            justification: null
        };
    }

    let lessonAnswersById = ref({});
    let courseModuleVideoLessonAnswers = ref([]);
    let loadingCourseModuleVideoLessonAnwsers = ref(false);
    async function getCourseModuleVideoLessonAnswers(){
        if(lessonId.value == undefined)
            return;
        loadingCourseModuleVideoLessonAnwsers.value = true;

        const response = await CourseVideoLessonAnswerService.getLessonAnswersById(lessonId.value);
        const values = response.getContent();

        for(let val of values){
            lessonAnswersById.value[val.id] = val;
        }
        loadingCourseModuleVideoLessonAnwsers.value = false;
        courseModuleVideoLessonAnswers.value = utils.parseToArray(values);
    }

    async function removeLessonAnswer(lessonAnswerId = ''){
        loading.value = true;
        const response = await await CourseVideoLessonAnswerService.removeLessonAnswer(lessonAnswerId) 
        loading.value = false;

        if(!response.success()){
            showToast(toast, response.getMessage(), 'error');
            return;
        }
        showToast(toast, response.getMessage());
        getCourseModuleVideoLessonAnswers(true);
    }

    let selectedLessonAnswerId = ref(null);
    function editLessonAnwser(id = null){
        selectedLessonAnswerId.value = id;
        if(lessonAnswersById.value[id] == undefined){
            showToast(toast, 'id not found', 'error');
            return;
        }
        selectedLessonAnswer.value = lessonAnswersById.value[id];
    }

    function resetSelectedLessonAnswer(){
        selectedLessonAnswerId.value = null;
        selectedLessonAnswer.value = {
            id: null, 
            correct: false,
            lesson: null,
            answer: null,
            justification: null
        };
    }
</script>

<style scoped>
    .no-bg-button{
        border: 1px solid var(--passion-fruit)!important;
        background-color: #fff!important;
        color: var(--passion-fruit)!important; 
    }
    .no-bg-button:hover{
        border: 1px solid var(--dark-passion-fruit)!important;
        color: var(--dark-passion-fruit)!important;
        transition: 0.2s ease;
    }
    .correct-button{
        background-color: #fff!important;
        color: var(--mint)!important;
    }
    .selected_answer{
        background-color: var(--mint)!important;
    }
</style>